import { default as LoadersBase } from "./base.js";
import { default as ModelsFrame } from "../model/frame.js";
import { default as ModelsStack } from "../model/stack.js";
import { default as ModelsSeries } from "../model/series.js";
import { default as ParserDicom } from "../parser/dicom.js";

function getLUT(pixelRepresentation, lutDataSet) {
  let numLUTEntries = lutDataSet.uint16("x00283002", 0);

  if (numLUTEntries === 0) {
    numLUTEntries = 65535;
  }
  let firstValueMapped = 0;

  if (pixelRepresentation === 0) {
    firstValueMapped = lutDataSet.uint16("x00283002", 1);
  } else {
    firstValueMapped = lutDataSet.int16("x00283002", 1);
  }
  const numBitsPerEntry = lutDataSet.uint16("x00283002", 2);
  const lut = {
    id: "1",
    firstValueMapped,
    numBitsPerEntry,
    lut: []
  };

  for (let i = 0; i < numLUTEntries; i++) {
    if (pixelRepresentation === 0) {
      lut.lut[i] = lutDataSet.uint16("x00283006", i);
    } else {
      lut.lut[i] = lutDataSet.int16("x00283006", i);
    }
  }

  return lut;
}

function getLUTs(pixelRepresentation, lutSequence) {
  if (!lutSequence || !lutSequence.items.length) {
    return;
  }
  const luts = [];

  for (let i = 0; i < lutSequence.items.length; i++) {
    const lutDataSet = lutSequence.items[i].dataSet;
    const lut = getLUT(pixelRepresentation, lutDataSet);

    if (lut) {
      luts.push(lut);
    }
  }

  return luts;
}

function getModalityLUTOutputPixelRepresentation(
  sopClassUID,
  rescaleIntercept,
  rescaleSlope,
  minStoredPixelValue,
  modalityLUTSequence,
  pixelRepresentation
) {
  // CT SOP Classes are always signed
  if (
    sopClassUID === "1.2.840.10008.5.1.4.1.1.2" ||
    sopClassUID === "1.2.840.10008.5.1.4.1.1.2.1"
  ) {
    return 1;
  }

  // if rescale intercept and rescale slope are present, pass the minimum stored
  // pixel value through them to see if we get a signed output range
  if (rescaleIntercept !== undefined && rescaleSlope !== undefined) {
    const minModalityLutValue =
      minStoredPixelValue * rescaleSlope + rescaleIntercept;

    if (minModalityLutValue < 0) {
      return 1;
    }

    return 0;
  }

  // Output of non linear modality lut is always unsigned
  if (modalityLUTSequence && modalityLUTSequence.length > 0) {
    return 0;
  }

  // If no modality lut transform, output is same as pixel representation
  return pixelRepresentation;
}

function isColorImage(photoMetricInterpretation) {
  return (
    photoMetricInterpretation === "RGB" ||
    photoMetricInterpretation === "PALETTE COLOR" ||
    photoMetricInterpretation === "YBR_FULL" ||
    photoMetricInterpretation === "YBR_FULL_422" ||
    photoMetricInterpretation === "YBR_PARTIAL_422" ||
    photoMetricInterpretation === "YBR_PARTIAL_420" ||
    photoMetricInterpretation === "YBR_RCT" ||
    photoMetricInterpretation === "YBR_ICT"
  );
}

function isModalityLUTForDisplay(sopClassUid) {
  // special case for XA and XRF
  // https://groups.google.com/forum/#!searchin/comp.protocols.dicom/Modality$20LUT$20XA/comp.protocols.dicom/UBxhOZ2anJ0/D0R_QP8V2wIJ
  return (
    sopClassUid !== "1.2.840.10008.5.1.4.1.1.12.1" && // XA
    sopClassUid !== "1.2.840.10008.5.1.4.1.1.12.2.1"
  ); // XRF
}

function parseUrl(url, base = "") {
  const parsedUrl = new URL(url, base);

  const data = {
    filename: parsedUrl.searchParams.get("filename"),
    extension: "",
    pathname: parsedUrl.pathname,
    query: parsedUrl.search
  };

  // get file name
  if (!data.filename) {
    data.filename = data.pathname.split("/").pop();
  }

  // find extension
  const splittedName = data.filename.split(".");
  data.extension = splittedName.length > 1 ? splittedName.pop() : "dicom";

  const skipExt = [
    "asp",
    "aspx",
    "go",
    "gs",
    "hs",
    "jsp",
    "js",
    "php",
    "pl",
    "py",
    "rb",
    "htm",
    "html"
  ];

  if (
    !isNaN(data.extension) ||
    skipExt.indexOf(data.extension) !== -1 ||
    (data.query && data.query.includes("contentType=application%2Fdicom"))
  ) {
    data.extension = "dicom";
  }

  return data;
}

export default class LoaderDicom extends LoadersBase {
  /**
   *
   * @param {*} response
   */

  parseFile(dataname, readFiles) {
    this.emit("parse-start", {
      file: dataname,
      time: new Date()
    });
    // give a chance to the UI to update because
    // after the rendering will be blocked with intensive JS
    // will be removed after eventer set up
    if (this._progressBar) {
      this._progressBar.update(0, 100, "parse", dataname);
    }

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(
          new Promise((resolve, reject) => {
            let data = readFiles;

            if (!Array.isArray(readFiles)) {
              data = [readFiles];
            }

            data.forEach(dataset => {
              this._preprocessFile(dataname, dataset);
            });

            if (data.length === 1) {
              data = data[0];
            } else {
              // if raw/mhd pair
              let mhdFile = data.filter(
                this._filterByExtension.bind(null, "MHD")
              );
              let rawFile = data.filter(
                this._filterByExtension.bind(null, "RAW")
              );
              if (
                data.length === 2 &&
                mhdFile.length === 1 &&
                rawFile.length === 1
              ) {
                data.url = mhdFile[0].url;
                data.extension = mhdFile[0].extension;
                data.mhdBuffer = mhdFile[0].buffer;
                data.rawBuffer = rawFile[0].buffer;
              }
            }

            let Parser = this._parser(data.extension);
            if (!Parser) {
              // emit 'parse-error' event
              this.emit("parse-error", {
                file: dataname,
                time: new Date(),
                error: data.filename + "can not be parsed."
              });
              reject(data.filename + " can not be parsed.");
            }

            // check extension
            let volumeParser = null;
            try {
              volumeParser = new Parser(data, 0);
              //volumeParser = new Parser(new Uint8Array(data), 0);
              //const dicomLoader_NS = new NSDicomLoaderAdapter(data);
              //volumeParser = new NSDicomLoaderAdapter(data);
              var a = 10;
            } catch (e) {
              console.warn(e);
              // emit 'parse-error' event
              this.emit("parse-error", {
                file: dataname,
                time: new Date(),
                error: e
              });
              reject(e);
            }

            // create a series
            let series = new ModelsSeries();
            // 업로드한 원본 파일명 저장
            series.filename = dataname;
            // global information
            series.seriesInstanceUID = volumeParser.seriesInstanceUID();
            series.transferSyntaxUID = volumeParser.transferSyntaxUID();
            series.seriesDate = volumeParser.seriesDate();
            series.seriesDescription = volumeParser.seriesDescription();
            series.seriesNumber = volumeParser.seriesNumber();

            series.studyInstanceUID = volumeParser.studyInstanceUID();
            series.studyID = volumeParser.studyID();
            series.studyDate = volumeParser.studyDate();
            series.studyTime = volumeParser.studyTime();
            series.studyDescription = volumeParser.studyDescription();
            series.numberOfFrames = volumeParser.numberOfFrames();
            if (!series.numberOfFrames) {
              series.numberOfFrames = 1;
            }
            series.numberOfChannels = volumeParser.numberOfChannels();
            series.modality = volumeParser.modality();
            // if it is a segmentation, attach extra information
            if (series.modality === "SEG") {
              // colors
              // labels
              // etc.
              series.segmentationType = volumeParser.segmentationType();
              series.segmentationSegments = volumeParser.segmentationSegments();
            }
            // patient information
            series.patientID = volumeParser.patientID();
            series.patientName = volumeParser.patientName();
            series.patientAge = volumeParser.patientAge();
            series.patientBirthdate = volumeParser.patientBirthdate();
            series.patientSex = volumeParser.patientSex();

            // Institution information
            series.institutionName = volumeParser.institutionName();
            series.institutionAddress = volumeParser.institutionAddress();

            // Machine information
            series.XRayTubeCurrent = volumeParser.XRayTubeCurrent();
            series.KVP = volumeParser.KVP();
            series.acquisitionDate = volumeParser.acquisitionDate();
            series.acquisitionTime = volumeParser.acquisitionTime();

            series.sliceThickness = volumeParser.sliceThickness();
            series.sliceLocation = volumeParser.sliceLocation();

            // Window Center/Width information
            series.windowCenter = volumeParser.windowCenter();
            series.windowWidth = volumeParser.windowWidth();

            // Image comment
            series.imageComment = volumeParser.imageComment();
            series.imageComment = volumeParser.studyComment();

            // information for MR modality
            series.fieldStrenth = volumeParser.fieldStrenth();
            series.repetitionTime = volumeParser.repetitionTime();
            series.echoTime = volumeParser.echoTime();

            // Orientation information
            // get row/colume Cosine
            let imageOrientationPatient = volumeParser.imageOrientation();
            let rowCosines = null;
            let columnCosines = null;
            if (imageOrientationPatient) {
              rowCosines = [
                parseFloat(imageOrientationPatient[0]),
                parseFloat(imageOrientationPatient[1]),
                parseFloat(imageOrientationPatient[2])
              ];
              columnCosines = [
                parseFloat(imageOrientationPatient[3]),
                parseFloat(imageOrientationPatient[4]),
                parseFloat(imageOrientationPatient[5])
              ];
            }

            series.rowCosines = rowCosines;
            series.columnCosines = columnCosines;
            series.imageOrientationPatient = imageOrientationPatient;
            series.instanceNumber = volumeParser.instanceNumber();

            // just create 1 dummy stack for now
            let stack = new ModelsStack();
            stack.numberOfChannels = volumeParser.numberOfChannels();
            stack.pixelRepresentation = volumeParser.pixelRepresentation();
            stack.pixelType = volumeParser.pixelType();
            stack.invert = volumeParser.invert();
            stack.spacingBetweenSlices = volumeParser.spacingBetweenSlices();
            stack.modality = series.modality;
            // if it is a segmentation, attach extra information
            if (stack.modality === "SEG") {
              // colors
              // labels
              // etc.
              stack.segmentationType = series.segmentationType;
              stack.segmentationSegments = series.segmentationSegments;
            }
            series.stack.push(stack);
            // recursive call for each frame
            // better than for loop to be able
            // to update dom with "progress" callback
            setTimeout(
              this.parseFrameClosure(
                series,
                stack,
                dataname,
                0,
                volumeParser,
                resolve,
                reject
              ),
              0
            );
          })
        );
      }, 10);
    });
  }

  /**
   * Parse response. (WADO)
   * response 포맷은 아래와 같다고 가정. :
   *    {
   *      url: 'resource url',
   *      buffer: xmlresponse,
   *    }
   * @param {object} response - response
   * @return {promise} promise
   */
  parse(response) {
    // emit 'parse-start' event
    this.emit("parse-start", {
      file: response.url,
      time: new Date()
    });

    // give a chance to the UI to update because
    // after the rendering will be blocked with intensive JS
    // will be removed after eventer set up
    if (this._progressBar) {
      this._progressBar.update(0, 100, "parse", response.url);
    }

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(
          new Promise((resolve, reject) => {
            let data = response;

            if (!Array.isArray(data)) {
              data = [data];
            }

            data.forEach(dataset => {
              this._preprocess(dataset);
            });

            if (data.length === 1) {
              data = data[0];
            } else {
              // if raw/mhd pair
              let mhdFile = data.filter(
                this._filterByExtension.bind(null, "MHD")
              );
              let rawFile = data.filter(
                this._filterByExtension.bind(null, "RAW")
              );
              if (
                data.length === 2 &&
                mhdFile.length === 1 &&
                rawFile.length === 1
              ) {
                data.url = mhdFile[0].url;
                data.extension = mhdFile[0].extension;
                data.mhdBuffer = mhdFile[0].buffer;
                data.rawBuffer = rawFile[0].buffer;
              }
            }

            let Parser = this._parser(data.extension);
            if (!Parser) {
              // emit 'parse-error' event
              this.emit("parse-error", {
                file: response.url,
                time: new Date(),
                error: data.filename + "can not be parsed."
              });
              reject(data.filename + " can not be parsed.");
            }

            // check extension
            let volumeParser = null;
            try {
              volumeParser = new Parser(data, 0);
            } catch (e) {
              console.warn(e);
              // emit 'parse-error' event
              this.emit("parse-error", {
                file: response.url,
                time: new Date(),
                error: e
              });
              reject(e);
            }

            // create a series
            let series = new ModelsSeries();
            // 원본 파일명 저장 (url 기반)
            series.filename = data.filename;
            // global information
            series.seriesInstanceUID = volumeParser.seriesInstanceUID();
            series.transferSyntaxUID = volumeParser.transferSyntaxUID();
            series.seriesDate = volumeParser.seriesDate();
            series.seriesDescription = volumeParser.seriesDescription();
            series.studyDate = volumeParser.studyDate();
            series.studyTime = volumeParser.studyTime();
            series.studyInstanceID = volumeParser.studyInstanceUID();
            series.studyDescription = volumeParser.studyDescription();
            series.numberOfFrames = volumeParser.numberOfFrames();
            if (!series.numberOfFrames) {
              series.numberOfFrames = 1;
            }
            series.numberOfChannels = volumeParser.numberOfChannels();
            series.modality = volumeParser.modality();
            // if it is a segmentation, attach extra information
            if (series.modality === "SEG") {
              // colors
              // labels
              // etc.
              series.segmentationType = volumeParser.segmentationType();
              series.segmentationSegments = volumeParser.segmentationSegments();
            }
            // patient information
            series.patientID = volumeParser.patientID();
            series.patientName = volumeParser.patientName();
            series.patientAge = volumeParser.patientAge();
            series.patientBirthdate = volumeParser.patientBirthdate();
            series.patientSex = volumeParser.patientSex();

            // just create 1 dummy stack for now
            let stack = new ModelsStack();
            stack.numberOfChannels = volumeParser.numberOfChannels();
            stack.pixelRepresentation = volumeParser.pixelRepresentation();
            stack.pixelType = volumeParser.pixelType();
            stack.invert = volumeParser.invert();
            stack.spacingBetweenSlices = volumeParser.spacingBetweenSlices();
            stack.modality = series.modality;
            // if it is a segmentation, attach extra information
            if (stack.modality === "SEG") {
              // colors
              // labels
              // etc.
              stack.segmentationType = series.segmentationType;
              stack.segmentationSegments = series.segmentationSegments;
            }
            series.stack.push(stack);
            // recursive call for each frame
            // better than for loop to be able
            // to update dom with "progress" callback
            setTimeout(
              this.parseFrameClosure(
                series,
                stack,
                response.url,
                0,
                volumeParser,
                resolve,
                reject
              ),
              0
            );
          })
        );
      }, 10);
    });
  }

  parseFrameClosure(series, stack, url, i, dataParser, resolve, reject) {
    return () => {
      this.parseFrame(series, stack, url, i, dataParser, resolve, reject);
    };
  }

  /**
   * recursive parse frame
   * @param {ModelsSeries} series - data series
   * @param {ModelsStack} stack - data stack
   * @param {string} url - resource url
   * @param {number} i - frame index
   * @param {parser} dataParser - selected parser
   * @param {promise.resolve} resolve - promise resolve args
   * @param {promise.reject} reject - promise reject args
   */
  parseFrame(series, stack, url, i, dataParser, resolve, reject) {
    let frame = new ModelsFrame();
    frame.sopInstanceUID = dataParser.sopInstanceUID(i);
    frame.sopClassUID = dataParser.sopClassUID(i);
    frame.url = url;
    frame.index = i;
    frame.invert = stack.invert;
    frame.frameTime = dataParser.frameTime(i);
    frame.ultrasoundRegions = dataParser.ultrasoundRegions(i);
    frame.rows = dataParser.rows(i);
    frame.columns = dataParser.columns(i);
    frame.numberOfChannels = stack.numberOfChannels;
    frame.pixelPaddingValue = dataParser.pixelPaddingValue(i);
    frame.pixelRepresentation = stack.pixelRepresentation;
    frame.pixelType = stack.pixelType;
    frame.pixelData = dataParser.extractPixelData(i);
    frame.pixelSpacing = dataParser.pixelSpacing(i);
    frame.performedProcedureStepID = dataParser.performedProcedureStepID();
    frame.spacingBetweenSlices = dataParser.spacingBetweenSlices(i);
    frame.sliceThickness = dataParser.sliceThickness(i);
    frame.imageOrientation = dataParser.imageOrientation(i);
    frame.rightHanded = dataParser.rightHanded();
    frame.acquisitionDate = dataParser.acquisitionDate();
    frame.acquisitionTime = dataParser.acquisitionTime();

    stack.rightHanded = frame.rightHanded;
    if (frame.imageOrientation === null) {
      frame.imageOrientation = [1, 0, 0, 0, 1, 0];
    }
    frame.imagePosition = dataParser.imagePosition(i);
    /*
        null ImagePosition should not be handle here
        if (frame.imagePosition === null) {
          frame.imagePosition = [0, 0, i];
        }*/
    frame.dimensionIndexValues = dataParser.dimensionIndexValues(i);
    frame.bitsAllocated = dataParser.bitsAllocated(i);
    frame.instanceNumber = dataParser.instanceNumber(i);
    frame.windowCenter = dataParser.windowCenter(i);
    frame.windowWidth = dataParser.windowWidth(i);
    frame.rescaleSlope = dataParser.rescaleSlope(i);
    frame.rescaleIntercept = dataParser.rescaleIntercept(i);

    // should pass frame index for consistency...

    frame.minMax = dataParser.minMaxPixelData(frame.pixelData);

    frame.photometricInterpretation = dataParser.photometricInterpretation();
    frame.VOILutSequence = dataParser.getVOILutSequence();
    frame.modalityLutSequence = dataParser.getModalityLUTSequence();
    // 아래는 위에서 파싱된 정보를 이용해 몇가지 2차 정보를 생성함.
    frame.color = isColorImage(frame.photometricInterpretation); // is color image?

    frame.frameOfReferenceUID = dataParser.frameOfReferenceUID();
    frame.studyDescription = dataParser.studyDescription();
    frame.seriesInstanceUID = dataParser.seriesInstanceUID();
    frame.patientName = dataParser.patientName();
    frame.modality = dataParser.modality();
    // 데이터 덤프 획득
    frame.dump = dataParser.dumpDataSet();
    // if series.mo
    if (series.modality === "SEG") {
      frame.referencedSegmentNumber = dataParser.referencedSegmentNumber(i);
    }

    // Modality LUT
    if (
      frame.pixelRepresentation &&
      frame.pixelRepresentation.length > 0 &&
      isModalityLUTForDisplay(frame.sopClassUID)
    ) {
      frame.modalityLUT = frame.pixelRepresentation[0];
    }

    //if (frame.isColorImage) {
    if (frame.color) {
      frame.windowWidth = 255;
      frame.windowCenter = 127;
    }

    // if windowCenter or windowWidth is not defined, set value.
    // set the ww/wc to cover the dynamic range of the image if no values are supplied
    if (frame.windowCenter === undefined || frame.windowWidth === undefined) {
      const maxVoi =
        Math.max(frame.minMax[0], frame.minMax[1]) * frame.rescaleSlope +
        frame.rescaleIntercept;
      const minVoi =
        Math.min(frame.minMax[0], frame.minMax[1]) * frame.rescaleSlope +
        frame.rescaleIntercept;

      frame.windowWidth = maxVoi - minVoi;
      frame.windowCenter = (maxVoi + minVoi) / 2;
    }

    const modalityLUTOutputPixelRepresentation = getModalityLUTOutputPixelRepresentation(
      frame.sopClassUid,
      frame.rescaleIntercept,
      frame.rescaleSlope,
      Math.min(frame.minMax[0], frame.minMax[1]),
      frame.modalityLutSequence,
      frame.pixelRepresentation
    );
    frame.voiLUTSequence = getLUTs(
      modalityLUTOutputPixelRepresentation,
      frame.getVOILutSequence
    );

    stack.frame.push(frame);

    // update status
    this._parsed = i + 1;
    this._totalParsed = series.numberOfFrames;

    // will be removed after eventer set up
    if (this._progressBar) {
      this._progressBar.update(this._parsed, this._totalParsed, "parse", url);
    }

    // emit 'parsing' event
    this.emit("parsing", {
      file: url,
      total: this._totalParsed,
      parsed: this._parsed,
      time: new Date()
    });

    if (this._parsed === this._totalParsed) {
      // emit 'parse-success' event
      this.emit("parse-success", {
        file: url,
        total: this._totalParsed,
        parsed: this._parsed,
        time: new Date()
      });
      series.numOfParsing = this._parsed;
      resolve(series);
    } else {
      setTimeout(
        this.parseFrameClosure(
          series,
          stack,
          url,
          this._parsed,
          dataParser,
          resolve,
          reject
        ),
        0
      );
    }
  }

  _parser(extension) {
    let Parser = null;

    switch (extension.toUpperCase()) {
      case "DICOM":
      case "DCM":
        Parser = ParserDicom;
        break;
      default:
        console.warn("warn : unspoorted extension : " + extension);
    }

    return Parser;
  }

  /**
   *
   */
  _preprocess(data) {
    // update data
    const parsedUrl = parseUrl(data.url);
    data.filename = parsedUrl.filename;
    data.extension = parsedUrl.extension;
    data.pathname = parsedUrl.pathname;
    data.query = parsedUrl.query;
    data.gzcompressed = false;
  }
  _preprocessFile(filename, data) {
    // update data
    data.filename = filename;
    data.extension = "DICOM";
    data.gzcompressed = false;
  }

  /**
   * Filter data by extension
   * @param {*} extension
   * @param {*} item
   * @returns Boolean
   */
  _filterByExtension(extension, item) {
    if (item.extension.toUpperCase() === extension.toUpperCase()) {
      return true;
    }
    return false;
  }
}
